import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, RotateCcw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

function getSessionId() {
  let id = localStorage.getItem('takumi_session_id');
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem('takumi_session_id', id);
  }
  return id;
}

const INITIAL_MESSAGE = {
  text: "Hello! I'm Takumi — your AI assistant for the Takumi ecosystem. Ask me about our platform, products, or services.",
  sender: 'bot',
};

/**
 * Parses a message string and returns an array of plain text and
 * clickable <a> elements for any [Label](/route) markdown links found.
 */
const renderMessageWithLinks = (text) => {
  const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = linkRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    const label = match[1];
    const href = match[2];
    const isExternal = href.startsWith('http');

    parts.push(
      <a
        key={match.index}
        href={href}
        target={isExternal ? '_blank' : '_self'}
        rel={isExternal ? 'noopener noreferrer' : undefined}
        className="underline font-medium hover:opacity-80 transition-opacity"
        style={{ color: 'inherit', textDecorationColor: 'currentColor' }}
      >
        {label}
      </a>
    );

    lastIndex = linkRegex.lastIndex;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length > 0 ? parts : [text];
};

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState(() => {
    const saved = localStorage.getItem('chatbot_messages');
    if (saved) {
      const parsed = JSON.parse(saved);
      // Always use the current INITIAL_MESSAGE; skip any stale cached greeting
      const rest = parsed[0]?.sender === 'bot' ? parsed.slice(1) : parsed;
      return [INITIAL_MESSAGE, ...rest];
    }
    return [INITIAL_MESSAGE];
  });
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Save messages to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('chatbot_messages', JSON.stringify(messages));
  }, [messages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isOpen]);

  const resetChat = () => {
    setMessages([INITIAL_MESSAGE]);
    setInput('');
    localStorage.removeItem('chatbot_messages');
    inputRef.current?.focus();
  };

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = { text: input, sender: 'user' };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-session-id': getSessionId(),
        },
        body: JSON.stringify({ message: input }),
      });

      const data = await response.json();

      if (!response.ok) {
        const errMsg = data.message || "Sorry, I'm having trouble right now. Please try again.";
        setMessages(prev => [...prev, { text: errMsg, sender: 'bot' }]);
      } else {
        setMessages(prev => [...prev, { text: data.reply, sender: 'bot' }]);
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error sending message:', error);
      setMessages(prev => [
        ...prev,
        { text: "Sorry, I'm having trouble connecting. Please try again later.", sender: 'bot' },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Floating Button */}
      <div className="fixed bottom-6 right-6 z-50">
        <div className="relative group">
          <button
            onClick={() => setIsOpen(true)}
            className="bg-takumi-red hover:bg-red-600 text-white p-4 rounded-full shadow-lg transition-all duration-300 hover:scale-110 relative"
            title="Chat with AI Assistant"
          >
            <MessageCircle size={24} />
            {/* Green dot indicator when chat is active/loading */}
            {isLoading && (
              <div className="absolute top-0 right-0 h-3 w-3 bg-green-400 rounded-full animate-pulse border border-white shadow-lg" />
            )}
          </button>
          <div className="absolute bottom-full right-0 mb-2 px-3 py-1 bg-gray-800 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap pointer-events-none">
            {isLoading ? 'Chat in progress...' : 'Chat with AI Assistant'}
          </div>
        </div>
      </div>

      {/* Backdrop + Modal */}
      <div
        className={`fixed inset-0 z-50 flex items-center justify-center py-4 transition-all duration-300 ${
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        style={{ backgroundColor: isOpen ? 'rgba(0,0,0,0.5)' : 'rgba(0,0,0,0)' }}
        onClick={(e) => { if (e.target === e.currentTarget) setIsOpen(false); }}
      >
        <div
          className={`bg-white dark:bg-gray-800 rounded-2xl shadow-2xl flex flex-col mx-4 transition-all duration-300 ease-out ${
            isOpen ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 translate-y-4'
          }`}
          style={{ width: '100%', maxWidth: '520px', height: '620px', maxHeight: 'calc(100vh - 32px)' }}
        >
          {/* Header */}
          <div className="bg-industrial-slate text-white px-5 py-4 rounded-t-2xl flex items-center justify-between flex-shrink-0">
            <div className="flex flex-col gap-0.5">
              <div className="flex items-center space-x-3">
                <div className="bg-white p-1.5 rounded-full shadow">
                  <img
                    src="https://customer-assets.emergentagent.com/job_22fe3f13-7547-4b99-be0c-1c48285a619d/artifacts/kkg75uhc_Takumi_logo.png"
                    alt="Takumi Logo"
                    className="h-6 w-6 object-contain"
                  />
                </div>
                <span className="font-semibold text-base leading-tight">Takumi</span>
              </div>
              <span className="text-xs text-gray-300 leading-tight pl-12">AI assistant for the Takumi ecosystem</span>
            </div>

            <div className="flex items-center space-x-1">
              <button
                onClick={resetChat}
                title="Restart conversation"
                className="hover:bg-white/20 p-2 rounded-lg transition-colors duration-200 group"
              >
                <RotateCcw
                  size={17}
                  className="transition-transform duration-300 group-hover:rotate-180"
                />
              </button>
              <button
                onClick={() => setIsOpen(false)}
                title="Close"
                className="hover:bg-red-600/80 p-2 rounded-lg transition-colors duration-200"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 scroll-smooth">
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[78%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed shadow-sm ${
                    msg.sender === 'user'
                      ? 'bg-takumi-red text-white rounded-br-sm'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-100 rounded-bl-sm'
                  }`}
                >
                  {renderMessageWithLinks(msg.text)}
                </div>
              </div>
            ))}

            {/* Typing indicator */}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-gray-100 dark:bg-gray-700 px-4 py-3 rounded-2xl rounded-bl-sm shadow-sm">
                  <div className="flex space-x-1.5 items-center">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="px-4 py-4 border-t dark:border-gray-700 flex-shrink-0 rounded-b-2xl">
            <div className="flex space-x-2 items-center">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask Takumi anything..."
                disabled={isLoading}
                className="flex-1 px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-takumi-red dark:bg-gray-700 dark:text-white transition-shadow duration-200 disabled:opacity-60"
              />
              <button
                onClick={sendMessage}
                disabled={isLoading || !input.trim()}
                className="bg-takumi-red hover:bg-red-600 text-white p-2.5 rounded-xl disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 hover:scale-105 active:scale-95 flex-shrink-0"
              >
                <Send size={18} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Chatbot;